import { useEffect, useRef, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'context/SettingsContext';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { withBaseCurrency, withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import InputQuantity from 'components/others/InputQuantity';
import ProductsSelect from '../../../Products/ProductsSelect';
import type { ProductSelectOption } from '../../../Products/types';
import type { SetEditFormValues, SetItemFormType } from '../../types';

interface SetFormItemsProps {
    formData: SetEditFormValues;
    onChange: (patch: Partial<SetEditFormValues>) => void;
    disabled?: boolean;
}

const emptyDraftItem = (): SetItemFormType => ({
    productId: 0,
    itemCount: 1,
});

const SetFormItems = ({ formData, onChange, disabled = false }: SetFormItemsProps) => {
    const { t } = useTranslation(['Sets']);
    const { localSettings } = useSettings();
    const quantityRef = useRef<HTMLInputElement>(null);
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const [draftItem, setDraftItem] = useState<SetItemFormType>(emptyDraftItem());
    const [selectedProduct, setSelectedProduct] = useState<ProductSelectOption | null>(null);

    const canAddItem = draftItem.productId > 0 && draftItem.itemCount > 0;

    useEffect(() => {
        if (!draftItem.productId) return;
        quantityRef.current?.focus();
    }, [draftItem.productId]);

    const renderLinePrice = (unitPrice: number, count: number) => {
        const total = (Number(unitPrice) || 0) * (Number(count) || 0);
        const formatted = formatPrice(total);
        const formattedUah = formatPrice(total * rate);

        if (isUahMode) {
            return { main: withLocalCurrency(formatted), title: undefined as string | undefined };
        }
        return { main: withBaseCurrency(formatted), title: withLocalCurrency(formattedUah) };
    };

    const handleAddItem = () => {
        if (!canAddItem) return;

        const existingIndex = formData.items.findIndex((item) => item.productId === draftItem.productId);

        if (existingIndex >= 0) {
            const nextItems = formData.items.map((item, index) => {
                if (index !== existingIndex) return item;
                return {
                    ...item,
                    itemCount: Math.round((item.itemCount + draftItem.itemCount) * 100) / 100,
                };
            });
            onChange({ items: nextItems });
        } else {
            onChange({
                items: [
                    ...formData.items,
                    {
                        ...draftItem,
                        prodName: selectedProduct?.label,
                        prodArticle: selectedProduct?.article,
                        vendorName: selectedProduct?.vendor,
                        defPrice: Number(selectedProduct?.defPrice || 0),
                        defPriceSale: Number(selectedProduct?.defPriceSale || 0),
                    },
                ],
            });
        }

        setDraftItem(emptyDraftItem());
        setSelectedProduct(null);
    };

    const handleRemoveItem = (productId: number) => {
        onChange({
            items: formData.items.filter((item) => item.productId !== productId),
        });
    };

    const adjustCount = (delta: number) => {
        setDraftItem((prev) => ({
            ...prev,
            itemCount: Math.max(1, Math.round((prev.itemCount + delta) * 100) / 100),
        }));
    };

    const handleCountChange = (rawValue: string) => {
        const parsed = Number(rawValue);
        setDraftItem((prev) => ({
            ...prev,
            itemCount: parsed > 0 ? parsed : 0,
        }));
    };

    return (
        <>
            {!disabled && (
            <>
            <ProductsSelect
                title={t('edit.fields.product')}
                required={false}
                value={draftItem.productId || null}
                onChange={(option) => {
                    setSelectedProduct(option);
                    setDraftItem((prev) => ({
                        ...prev,
                        productId: option?.value ? Number(option.value) : 0,
                    }));
                }}
            />

            <InputQuantity
                ref={quantityRef}
                label={t('edit.fields.itemCount')}
                className="mb-3"
                value={draftItem.itemCount}
                onDown={() => adjustCount(-1)}
                onUp={() => adjustCount(1)}
                onChange={handleCountChange}
                onEnter={handleAddItem}
                inputGroupClassName="SetFormQuantityGroup flex-shrink-0"
                buttonClassName="SetFormQuantityBtn"
                inputClassName="text-center SetFormQuantityInput"
                decreaseAriaLabel={t('edit.decreaseCount')}
                increaseAriaLabel={t('edit.increaseCount')}
                actionsClassName="d-flex gap-1 align-items-stretch SetFormAddRow"
                actions={
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        className="flex-grow-1 SetFormAddBtn"
                        disabled={!canAddItem}
                        onClick={handleAddItem}
                    >
                        <i className="bi bi-plus me-1" />
                        <span className="SetFormAddBtnText">{t('edit.addItem')}</span>
                    </Button>
                }
            />
            </>
            )}

            {formData.items.length > 0 ? (
                <div className="crm-table-wrap">
                    <Table size="sm" className="SetItemsTable mb-0">
                        <thead>
                            <tr className="bg-light">
                                <td className="head-product">{t('edit.fields.product')}</td>
                                <td className="head-price">{t('fields.priceList')}</td>
                                <td className="head-count text-end">{t('edit.fields.itemCount')}</td>
                                {!disabled && <td className="head-action"></td>}
                            </tr>
                        </thead>
                        <tbody>
                            {formData.items.map((item) => {
                                const purchase = renderLinePrice(item.defPrice || 0, item.itemCount);
                                const sale = renderLinePrice(item.defPriceSale || 0, item.itemCount);

                                return (
                                    <tr key={item.productId}>
                                        <td className="product">
                                            <div className="fw-medium">{item.prodName || `#${item.productId}`}</div>
                                            {item.vendorName ? (
                                                <small className="text-muted">{item.vendorName}</small>
                                            ) : null}
                                        </td>
                                        <td className="price text-truncate">
                                            <h6 className="mb-1" title={purchase.title}>
                                                {purchase.main}
                                            </h6>
                                            <p className="f-12 mb-0 text-primary text-truncate" title={sale.title}>
                                                {sale.main}
                                            </p>
                                        </td>
                                        <td className="count text-end">{item.itemCount}</td>
                                        {!disabled && (
                                        <td className="action text-end">
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="text-danger p-0"
                                                onClick={() => handleRemoveItem(item.productId)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <p className="text-muted mb-0">{t('edit.itemsEmpty')}</p>
            )}
        </>
    );
};

export default SetFormItems;
